/**
 * Network Card Component
 * Displays individual network information with actions
 */

import React, { useState } from 'react';
import { RefreshCw, TrendingUp, Edit, Trash2, CheckCircle, XCircle, Clock, AlertCircle, History, Tag, Layers, Calendar, Users, DollarSign, UserPlus, RotateCcw, Crosshair } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as Checkbox from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { useSyncNetwork, useDeleteNetwork, useHistoricalSync, useTrackerCheck } from '../../hooks/useNetworksData';
import { useNetworksStore } from '../../stores/networksStore';
import { SyncProgressIndicator } from './SyncProgressIndicator';
import { EditNetworkModal } from './EditNetworkModal';
import type { NetworkCardData } from '../../types/networks.types';
import { toast } from 'sonner';

interface NetworkCardProps {
  network: NetworkCardData;
  isSelected: boolean;
}

export const NetworkCard: React.FC<NetworkCardProps> = ({ network, isSelected }) => {
  const navigate = useNavigate();
  const { toggleNetworkSelection, syncProgress } = useNetworksStore();
  const syncMutation = useSyncNetwork();
  const deleteMutation = useDeleteNetwork();
  const historicalSyncMutation = useHistoricalSync();
  const trackerCheckMutation = useTrackerCheck();
  const trackerCheckEnabled = Boolean(network.metadata?.tracker_check);
  const trackerCheckLast = network.metadata?.tracker_check_last as
    | { at: string; ok: boolean; error?: string; filled?: { reg: number; ftd: number }; players?: number }
    | undefined;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Summary data is now included inline from the networks API - no separate request needed
  const summary = (network as any).summary;
  const summaryLoading = false;
  
  const [isPlayerSyncing, setIsPlayerSyncing] = useState(false);
  const [isForceResyncing, setIsForceResyncing] = useState(false);
  
  const currentSyncProgress = syncProgress[network.id];
  const isSyncing = currentSyncProgress?.status === 'syncing' || network.sync_status === 'running';
  const isHistoricalSyncing = historicalSyncMutation.isPending;

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync(network.id);
      toast.success('Sync started successfully');
    } catch (error) {
      toast.error('Failed to start sync');
    }
  };

  const handleHistoricalSync = async () => {
    try {
      // Use sync_from_date from metadata for full history, or default to 2023-01-01
      // Don't pass startDate to let the API use metadata's sync_from_date
      toast.info(`Starting full historical sync... This may take a few minutes.`);
      const result = await historicalSyncMutation.mutateAsync({ 
        networkId: network.id,
        force: false // Only sync months that haven't been synced yet
      });
      if (result.success) {
        const stats = result.summary?.total_records_inserted || 0;
        const players = result.summary?.total_players_synced || 0;
        const playerError = result.summary?.player_sync_error;
        
        let message = `Historical sync complete! ${stats} stats records imported.`;
        if (players > 0) {
          message += ` ${players} players synced.`;
        }
        toast.success(message);
        
        // Show player sync error as separate warning if present
        if (playerError) {
          toast.warning(`Player sync issue: ${playerError}`);
        }
        
        console.log('Full sync result:', JSON.stringify(result, null, 2));
      } else {
        toast.error(result.error || 'Historical sync failed');
      }
    } catch (error: any) {
      console.error('Historical sync error:', error);
      // Try to get the API error response details
      const errorMessage = error.message || 'Failed to start historical sync';
      toast.error(errorMessage);
      
      // Log the full error details for debugging
      if (error.debugSteps) {
        console.log('Debug steps:', error.debugSteps);
      }
    }
  };

  const handlePlayerSync = async () => {
    try {
      setIsPlayerSyncing(true);
      toast.info('Syncing all players... This may take a few minutes.');
      
      // Call historical sync with skipStats=true, skipPlayers=false to ONLY sync players
      const result = await historicalSyncMutation.mutateAsync({ 
        networkId: network.id,
        force: false,
        skipPlayers: false, // Sync players!
        skipStats: true // Skip stats, only sync players
      });
      
      if (result.success) {
        const players = result.summary?.total_players_synced || 0;
        const playerError = result.summary?.player_sync_error;
        
        if (players > 0) {
          toast.success(`Player sync complete! ${players} players synced.`);
        } else if (playerError) {
          toast.warning(`Player sync issue: ${playerError}`);
        } else {
          toast.info('Player sync complete. No new players found.');
        }
        
        console.log('Player sync result:', JSON.stringify(result, null, 2));
      } else {
        toast.error(result.error || 'Player sync failed');
      }
    } catch (error: any) {
      console.error('Player sync error:', error);
      toast.error(error.message || 'Failed to sync players');
    } finally {
      setIsPlayerSyncing(false);
    }
  };

  const handleForceResync = async () => {
    try {
      setIsForceResyncing(true);
      toast.info('Starting force resync... This will re-fetch ALL historical data including commissions. May take several minutes.');
      
      // Call historical sync with force=true to resync all months
      const result = await historicalSyncMutation.mutateAsync({ 
        networkId: network.id,
        force: true, // Force resync all months even if already synced
        skipPlayers: true // Skip players to speed up, user can sync separately
      });
      
      if (result.success) {
        const stats = result.summary?.total_records_inserted || 0;
        const monthsSynced = result.summary?.months_synced_now || 0;
        
        toast.success(`Force resync complete! ${monthsSynced} months re-synced, ${stats} stats records updated with commissions.`);
        
        console.log('Force resync result:', JSON.stringify(result, null, 2));
      } else {
        toast.error(result.error || 'Force resync failed');
      }
    } catch (error: any) {
      console.error('Force resync error:', error);
      toast.error(error.message || 'Failed to force resync');
    } finally {
      setIsForceResyncing(false);
    }
  };

  const handleTrackerCheck = async () => {
    try {
      const result = await trackerCheckMutation.mutateAsync({ networkId: network.id });
      const reg = result.filled?.reg ?? 0;
      const ftd = result.filled?.ftd ?? 0;
      if (reg + ftd > 0) {
        toast.success(`Tracker check: added ${reg} REG and ${ftd} FTD the tracker had missed (${result.players} players checked).`);
      } else {
        toast.success(`Tracker check: all ${result.matched ?? 0} matched players were already in the tracker.`);
      }
      if (result.unknownClickIds?.length) {
        toast.warning(`${result.unknownClickIds.length} payload(s) did not match a tracker click.`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Tracker check failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(network.id);
      toast.success('Network deleted successfully');
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error('Failed to delete network');
    }
  };

  const getStatusBadge = () => {
    const baseClasses = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium";
    
    switch (network.statusColor) {
      case 'green':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`}>
            <CheckCircle className="w-3 h-3" />
            {network.statusLabel}
          </span>
        );
      case 'yellow':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400`}>
            <Clock className="w-3 h-3 animate-pulse" />
            {network.statusLabel}
          </span>
        );
      case 'red':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`}>
            <XCircle className="w-3 h-3" />
            {network.statusLabel}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400`}>
            <AlertCircle className="w-3 h-3" />
            {network.statusLabel}
          </span>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-all duration-200 hover:shadow-md relative">
      {/* Checkbox and Header */}
      <div className="flex items-start gap-4 mb-4">
        <Checkbox.Root
          checked={isSelected}
          onCheckedChange={() => toggleNetworkSelection(network.id)}
          className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 flex items-center justify-center"
          aria-label={`Select ${network.account_name}`}
        >
          <Checkbox.Indicator>
            <Check className="w-3 h-3 text-white" />
          </Checkbox.Indicator>
        </Checkbox.Root>

        {/* Platform Logo */}
        {network.platform_logo ? (
          <img
            src={network.platform_logo}
            alt={network.platform_name}
            className="w-24 h-10 object-contain"
          />
        ) : (
          <div className="w-24 h-10 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {network.platform_name}
            </span>
          </div>
        )}

        <div className="flex-1" />
        {getStatusBadge()}
      </div>

      {/* Account Name and Details */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {network.account_name}
        </h3>
        {/* Platform Provider (Affiliate System) and API Key indicator */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {network.platform_provider && (
            <div className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {network.platform_provider}
              </span>
            </div>
          )}
          {/* API Key/Token indicator */}
          {network.has_api_key !== undefined && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
              network.has_api_key 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
            }`}>
              {network.has_api_key ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Token
                </>
              ) : (
                <>
                  <AlertCircle className="w-3 h-3" />
                  No Token
                </>
              )}
            </span>
          )}
          {/* Currency indicator */}
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
            network.currency 
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
          }`}>
            <DollarSign className="w-3 h-3" />
            {network.currency || 'Unset'}
          </span>
          {/* 2FA Status indicator */}
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
            network.requires_2fa === true 
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
              : network.requires_2fa === false
                ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            2FA: {network.requires_2fa === true ? 'Yes' : network.requires_2fa === false ? 'No' : 'Auto'}
          </span>
        </div>
        {/* Username */}
        {network.username && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            <span className="font-medium">User:</span> {network.username}
          </p>
          )}
        {/* Tags */}
        {network.tags && network.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <Tag className="w-3 h-3 text-gray-400" />
            {network.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Current Balance - This month's earnings from summary */}
      <div className="mb-4">
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Current Balance</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {summaryLoading ? '...' : new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
          }).format(summary?.current_balance || 0)}
        </p>
      </div>

      {/* Last Sync */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Last synced: {network.lastSyncFormatted}
        </p>
        {trackerCheckEnabled && (
          <p
            className={`text-xs mt-1 ${trackerCheckLast && !trackerCheckLast.ok ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
            title={trackerCheckLast?.error}
          >
            Tracker check:{' '}
            {!trackerCheckLast
              ? 'not run yet'
              : trackerCheckLast.ok
                ? `${new Date(trackerCheckLast.at).toLocaleString()} · added ${trackerCheckLast.filled?.reg ?? 0} REG, ${trackerCheckLast.filled?.ftd ?? 0} FTD`
                : `failed ${new Date(trackerCheckLast.at).toLocaleString()}: ${trackerCheckLast.error ?? 'unknown error'}`}
          </p>
        )}
      </div>

      {/* Sync Summary Stats */}
      {summary && summary.has_data && (
        <div className="mb-4 grid grid-cols-3 gap-2">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Calendar className="w-3 h-3 text-blue-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Months</span>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {summary.months_synced}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="w-3 h-3 text-green-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Players</span>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {summary.total_players.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="w-3 h-3 text-emerald-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Comm.</span>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              €{summary.total_commission.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}
      
      {/* Summary Loading State */}
      {summaryLoading && (
        <div className="mb-4 grid grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
            </div>
          ))}
        </div>
      )}

      {/* Sync Error Message - Show for both error and partial status */}
      {(network.sync_status === 'error' || network.sync_status === 'partial') && network.sync_error_message && (
        <div className={`mb-4 p-3 rounded-lg border ${
          network.sync_status === 'error' 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-start gap-2">
            <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
              network.sync_status === 'error' 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-yellow-600 dark:text-yellow-400'
            }`} />
            <p className={`text-xs ${
              network.sync_status === 'error' 
                ? 'text-red-800 dark:text-red-300' 
                : 'text-yellow-800 dark:text-yellow-300'
            }`}>
              {network.sync_status === 'partial' ? 'Partial sync: ' : ''}{network.sync_error_message}
            </p>
          </div>
        </div>
      )}

      {/* Sync Progress */}
      {currentSyncProgress && currentSyncProgress.status === 'syncing' && (
        <div className="mb-4">
          <SyncProgressIndicator progress={currentSyncProgress} variant="inline" />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleSync}
          disabled={isSyncing || syncMutation.isPending || isHistoricalSyncing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sync now"
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Sync Now'}
        </button>

        <button
          onClick={handleHistoricalSync}
          disabled={isSyncing || isHistoricalSyncing || isPlayerSyncing || isForceResyncing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Fetch historical data"
          title="Fetch all data from 2023 onwards"
        >
          <History className={`w-4 h-4 ${isHistoricalSyncing || isForceResyncing ? 'animate-spin' : ''}`} />
          {isHistoricalSyncing ? 'Fetching...' : isForceResyncing ? 'Resyncing...' : 'Fetch History'}
        </button>

        <button
          onClick={handlePlayerSync}
          disabled={isSyncing || isHistoricalSyncing || isPlayerSyncing || isForceResyncing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sync all players"
          title="Fetch all historical players (may take longer)"
        >
          <UserPlus className={`w-4 h-4 ${isPlayerSyncing ? 'animate-spin' : ''}`} />
          {isPlayerSyncing ? 'Syncing...' : 'Sync Players'}
        </button>

        <button
          onClick={handleForceResync}
          disabled={isSyncing || isHistoricalSyncing || isPlayerSyncing || isForceResyncing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Force resync all data"
          title="Re-fetch ALL historical data including commissions (use if data is incorrect)"
        >
          <RotateCcw className={`w-4 h-4 ${isForceResyncing ? 'animate-spin' : ''}`} />
          {isForceResyncing ? 'Resyncing...' : 'Force Resync'}
        </button>

        {trackerCheckEnabled && (
          <button
            onClick={handleTrackerCheck}
            disabled={trackerCheckMutation.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg hover:bg-cyan-100 dark:hover:bg-cyan-900/30 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Check tracker"
            title="Add registrations and first deposits from the player reports that the tracker never got as a postback (last 30 days)"
          >
            <Crosshair className={`w-4 h-4 ${trackerCheckMutation.isPending ? 'animate-spin' : ''}`} />
            {trackerCheckMutation.isPending ? 'Checking...' : 'Check Tracker'}
          </button>
        )}

        <button
          onClick={() => navigate(`/networks/${network.id}/stats`)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
          aria-label="View statistics"
        >
          <TrendingUp className="w-4 h-4" />
          View Stats
        </button>

        <button
          onClick={() => setShowEditModal(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
          aria-label="Edit network"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
            aria-label="Delete network"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      
      {/* Edit Modal - placed outside button container */}
      <EditNetworkModal
        network={network}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
      />
    </div>
  );
};
